/**
 * A timeline: one lane per object against a shared axis.
 *
 * `ACCEPTANCE.md`'s Notion row asks for "databases with every view type", and
 * timeline is the sixth and last. It is the one that needed a vocabulary change
 * to be honest.
 *
 * **Why a bar could not be built from the roles that already existed.** A span
 * has two ends. Two manifest keys may both bind role `date`, and an object
 * populating both returns that role as an ARRAY — two values in no particular
 * order, which is why the calendar counts such an object as undated rather than
 * guessing which day it happens on. "These two dates, in this order" is a
 * different claim, so `date.start` and `date.end` are words now.
 *
 * **The host owns the scale.** Positions cross into the module as fractions of
 * the axis, never as dates. Handing the renderer two ISO strings would make it
 * parse dates, which is the job the calendar already proved has a time-zone
 * trap in it — and it would make every bar do that arithmetic separately, so
 * two bars could disagree about where the axis starts.
 */
import {
  CAPABILITIES, ROLE_KIND, type Role, type TLayoutSpec,
} from '@morph/spec';
import type { ObjectShape } from '@morph/generation';
import {
  handleOf, type BoundValues, type CapabilityInputs, type IR, type ObjectHandle,
} from '@morph/runtime';
import { calendarDay, calendarLabel } from './calendar.js';
import { withoutViewState } from './capability-view.js';

/** Which roles a timeline reads. `start`/`end` make a bar; a bare `date` makes
 *  a moment. A shape may have any of them, and the answer says which. */
export function timelineRoles(shape: ObjectShape): {
  start?: Role; end?: Role; date?: Role; lane?: Role;
} {
  const has = (role: Role) => shape.roles.some((r) => r.role === role);
  const enums = shape.roles.filter((r) => ROLE_KIND[r.role] === 'enum');
  return {
    ...(has('date.start') ? { start: 'date.start' as Role } : {}),
    ...(has('date.end') ? { end: 'date.end' as Role } : {}),
    ...(has('date') ? { date: 'date' as Role } : {}),
    // A lane groups bars the way a board's columns group cards. `status` first
    // because that is what a board groups by, so the two views agree.
    ...(enums.find((r) => r.role === 'status') ?? enums[0]
      ? { lane: (enums.find((r) => r.role === 'status') ?? enums[0])!.role }
      : {}),
  };
}

/**
 * A calendar day as a number, for arithmetic only.
 *
 * `Date.UTC` on the three parts read out of the string — never `new Date(str)`,
 * which reads a plain `YYYY-MM-DD` as UTC midnight and then reports the day
 * before for any reader west of Greenwich. The same trap `calendarDay` avoids;
 * this needs the ordinal rather than the string, so it takes the parts from
 * `calendarDay` and does the one safe conversion.
 */
export function dayNumber(v: unknown): number | undefined {
  const day = calendarDay(v);
  if (!day) return undefined;
  return Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1,
                  Number(day.slice(8, 10))) / 86_400_000;
}

/** Replace a lens with a timeline over the same scope. */
export function timelineSpec(spec: TLayoutSpec, shape: ObjectShape): TLayoutSpec {
  const { start, end, date, lane } = timelineRoles(shape);
  const label = calendarLabel(shape);
  const bind = (slot: string, role: Role) =>
    ({ [slot]: { slot: `tl-${slot}`, role, direction: 'read' as const } });
  return {
    // Controls dropped with the root they drove — see `withoutViewState`.
    ...withoutViewState(spec),
    target: { ...spec.target, cardinality: 'large-set' },
    root: {
      kind: 'capability',
      id: 'pool-timeline',
      capability: 'timeline',
      version: CAPABILITIES['timeline'].version,
      config: { granularity: 'month', showToday: false },
      channels: {
        ...(start ? bind('start', start) : {}),
        ...(end ? bind('end', end) : {}),
        ...(date ? bind('date', date) : {}),
        ...(label ? bind('label', label) : {}),
        ...(lane ? bind('lane', lane) : {}),
      },
    },
  } as TLayoutSpec;
}

/** How an axis end reads. A year when the span is long, a month when it is not
 *  — a timeline over sixty years labelled to the day is noise, and one over a
 *  fortnight labelled by year says nothing at all. */
export function axisLabel(day: number, spanDays: number): string {
  const iso = new Date(day * 86_400_000).toISOString().slice(0, 10);
  if (spanDays > 1200) return iso.slice(0, 4);
  if (spanDays > 70) return iso.slice(0, 7);
  return iso;
}

/**
 * Where each object sits on one shared axis.
 *
 * Every bar is a fraction of the same span, computed once here. An object with
 * a start and an end is a bar; one with a single date is a moment; one with
 * neither is counted and SAID, never dropped.
 */
export function timelineInputs(
  ir: IR, rows: readonly BoundValues[],
  /* `undefined` for a reader — task `0182`. The module input's epoch is
     optional now, and this threads the host's MAY_WRITE gate through. */
  epoch: number | undefined,
  /** The lanes in the host's DECLARED order — a status property's options —
   *  so the module colours a lane as the board tints its column, whatever
   *  order the bars sort into by date. Absent, the module goes first-seen. */
  lanes?: readonly string[],
): CapabilityInputs | undefined {
  const node = ir.nodes.find((n) => n.kind === 'capability' && n.capability === 'timeline');
  if (!node) return undefined;
  const ch = (name: string) => node.channels?.[name]?.role as Role | undefined;
  const startRole = ch('start');
  const endRole = ch('end');
  const dateRole = ch('date');
  const labelRole = ch('label');
  const laneRole = ch('lane');

  type Raw = {
    from: number; to: number; label: string;
    lane?: string; open?: boolean; handle?: ObjectHandle;
  };
  const raw: Raw[] = [];
  let undated = 0;

  for (const row of rows) {
    // An array-valued date role is two values in no particular order. Counted
    // as undated rather than resolved, the same call the calendar makes — and
    // the reason `date.start`/`date.end` exist at all.
    const arrayed = [startRole, endRole, dateRole]
      .some((r) => r !== undefined && Array.isArray(row[r]));
    if (arrayed) { undated += 1; continue; }

    const s = startRole === undefined ? undefined : dayNumber(row[startRole]);
    const e = endRole === undefined ? undefined : dayNumber(row[endRole]);
    const d = dateRole === undefined ? undefined : dayNumber(row[dateRole]);
    const from = s ?? d;
    if (from === undefined) { undated += 1; continue; }
    // A start with NO END is unfinished. It still gets a bar — running to
    // whatever the object was last known at — but it is marked open, because a
    // bar that stops at "last touched" is otherwise indistinguishable from one
    // that stopped because the work was done, and drawing them the same way is
    // the chart telling a reader something untrue. Measured before this: two
    // in-progress tasks rendered exactly like the ten finished ones.
    const open = s !== undefined && e === undefined;
    const to = e ?? (s === undefined ? from : d ?? from);

    const rawLabel = labelRole === undefined ? undefined : row[labelRole];
    const label = typeof rawLabel === 'string' && rawLabel.trim()
      ? rawLabel.trim() : 'Untitled';
    const rawLane = laneRole === undefined ? undefined : row[laneRole];
    const handle = handleOf(row);
    raw.push({
      // An end before its start is bad data, not a negative bar. Ordered here
      // so the module never has to decide what a backwards span means.
      from: Math.min(from, to), to: Math.max(from, to), label,
      ...(open ? { open: true } : {}),
      ...(typeof rawLane === 'string' && rawLane.trim() ? { lane: rawLane.trim() } : {}),
      ...(handle ? { handle } : {}),
    });
  }

  if (!raw.length) {
    return new Map([[node.i, {
      capability: 'timeline' as const,
      timeline: { from: '', to: '', bars: [], ...(epoch === undefined ? {} : { epoch }), undated },
    }]]);
  }

  const lo = Math.min(...raw.map((b) => b.from));
  const hi = Math.max(...raw.map((b) => b.to));
  // A span of ZERO — every object on the same day — would divide by nothing.
  // One day of width puts them all at the left end, which is the truth.
  const span = hi - lo || 1;
  const bars = raw
    .sort((a, b) => a.from - b.from || a.label.localeCompare(b.label))
    .map((b) => ({ ...b, from: (b.from - lo) / span, to: (b.to - lo) / span }));

  return new Map([[node.i, {
    capability: 'timeline' as const,
    timeline: {
      ...(lanes?.length ? { lanes } : {}),
      from: axisLabel(lo, hi - lo),
      to: axisLabel(hi, hi - lo),
      bars, ...(epoch === undefined ? {} : { epoch }), undated,
    },
  }]]);
}
